import React, { useState } from 'react';
import mustache from 'mustache';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../../../hooks/Analytics';
import { NotificationPreferences } from '../../../../../../../../__generated__/loma';
import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import TextInput from '../../../../../ui/TextInput/TextInput';
import Checkbox from '../../../../../ui/Checkbox/Checkbox';
import { useTranslations } from '../../../../../../hooks/useTranslations';
import { useTheme } from '../../../../../../hooks/useTheme';
import { LayoutTree } from '../../../../DynamicLayout';
import { PreferenceLevels } from '../../../types';
import { useGlobalSettings } from '../../../../../../hooks/useGlobalSettings';
import styles from './EmailOptInForm.module.css';

const { FeatureLayout } = LayoutTree;

export interface EmailOptInProps {
  onOptIn: (contactInfo: string, retailerLevel: boolean) => void;
  featureName?: string;
  validationError?: boolean;
  layout?: LayoutTree.FeatureLayout;
  notificationPreferencesState?: NotificationPreferences;
}

const EmailOptIn = ({
  onOptIn,
  featureName,
  validationError,
  layout,
  notificationPreferencesState,
}: EmailOptInProps) => {
  const { preferenceLevel } = notificationPreferencesState || {};
  const [retailerLevelChecked, setRetailerLevelChecked] = useState(
    preferenceLevel === PreferenceLevels.RETAILER,
  );
  const [email, setEmail] = useState('');

  const { retailerLevelOptInEnabled, retailerName } = useGlobalSettings();

  const {
    bodytextDefaultNone: bodyTextStyle,
    captiontextErrorNone: errorTextStyle,
  } = useTheme();

  const {
    notificationCenterEmailInputLabel,
    notificationCenterEmailSignupButton,
    notificationCenterEmailValidationErrorMessage,
    notificationCenterRetailerLevelSubscribeOption,
  } = useTranslations();

  const logEvent = useAnalyticsPost();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  return (
    <form
      noValidate
      onSubmit={(e): void => {
        e.preventDefault();
        logEvent({
          clickCategory: NarvarClickSubTypes.EMAIL_SIGNUP,
          clickNotificationIsOptIn: true,
          clickSubType: NarvarClickSubTypes.EMAIL_SIGNUP,
          clickType: NarvarClickTypes.INTERACTION,
          eventComponent:
            NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
          eventFeature:
            NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
          eventType: NarvarEventTypes.CLICK,
          feature: featureName,
          pageComponent:
            NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
        });
        onOptIn(email, retailerLevelChecked);
      }}
    >
      <div className={styles.emailOptinFormContainer}>
        <div
          data-testid={`column-one-${isLandscape ? '7' : '12'}`}
          style={{
            gridColumn: `span ${isLandscape ? '7' : '12'}`,
            marginBottom: isLandscape ? '1rem' : undefined,
          }}
        >
          <TextInput
            id={notificationCenterEmailInputLabel || ''}
            label={notificationCenterEmailInputLabel || ''}
            animateLabel
            title={notificationCenterEmailInputLabel || ''}
            centered={!isLandscape}
            error={validationError}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              setEmail(e.target.value)
            }
            containerOverridesStyle={
              validationError
                ? {
                    border: `1px solid ${errorTextStyle?.color ?? ''}`,
                  }
                : undefined
            }
          />
          {validationError && (
            <div
              data-test="emailOptInValidationErrorContainer"
              style={{ ...bodyTextStyle, ...errorTextStyle }}
            >
              {notificationCenterEmailValidationErrorMessage}
            </div>
          )}
          {retailerLevelOptInEnabled &&
            (preferenceLevel === PreferenceLevels.RETAILER ||
              preferenceLevel === PreferenceLevels.ORDER) && (
              <Checkbox
                id="retailerLevel"
                onChange={(): void =>
                  setRetailerLevelChecked(!retailerLevelChecked)
                }
                checked={retailerLevelChecked}
                containerStyle={{ marginTop: '1rem' }}
                labelTextCellStyle={{ textAlign: 'left' }}
              >
                {mustache.render(
                  notificationCenterRetailerLevelSubscribeOption || '',
                  { retailerName },
                )}
              </Checkbox>
            )}
        </div>
        <div
          data-testid={`column-two-${isLandscape ? '5' : '12'}`}
          style={{
            gridColumn: `span ${isLandscape ? '5' : '12'}`,
          }}
        >
          <Button big type={ButtonType.submit}>
            {notificationCenterEmailSignupButton}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EmailOptIn;
